// FileCard — file-explorer tile for a single file (Excalidraw or draw.io).
//
// Visual: a sheet of paper with the top-right corner *torn off*. The
// card reads as paper, not a flat panel:
//
//   - Sheet body  → `--color-card` with a soft drop shadow lifting it
//                   off the desk, outlined with `--color-card-stroke`.
//                   The silhouette is a rectangle minus a jagged
//                   torn-off corner (see `tornCorner.ts`).
//   - Paper grain → `.bg-paper-grain` overlay (theme-aware multiply /
//                   screen blend) gives the surface its "tooth". The
//                   same utility powers `<PaperSurface>` so cards and
//                   the desk share visual vocabulary.
//   - Paper dots  → `.bg-paper-dots` adds discrete fibre specks on top
//                   of the grain for a recycled / kraft-paper feel.
//   - Back stack  → two extra sheets that fan out from behind on hover,
//                   each torn with the SAME tear polygon as the front
//                   so the stack reads as "a notepad torn through"
//                   rather than three independently-torn pages.
//
// Geometry: a single torn-corner polygon (deterministic per file id)
// drives both the rough.js silhouette and the `clip-path` of the HTML
// overlays. The thumbnail therefore stops cleanly at the torn edge —
// nothing peeks past the silhouette.
//
// Top-left cluster: a permanent `<FileKindBadge>` sits at the top-left
// of every card (so the user can tell Excalidraw vs draw.io at a
// glance). When the file has active share tokens, a `<SharePill>` sits
// just to its right. Both share one absolute container so they never
// stack on top of each other.
//
// Interaction model (parity with `FolderCard`):
//   - single click  → opens the file (`onOpen`)
//   - right click   → context menu via the consumer's `<ContextMenuTrigger>`
//   - keyboard      → Tab focuses the card; Enter / F2 / Delete are
//                     consumed by `useExplorerHotkeys` based on which
//                     card has focus.
//
// Hover animation (see `.ink-file__*` in `index.css`):
//   - back-left and back-right sheets fade in and rotate outward (inset
//     from the front sheet at rest so they peek from *behind*, not beyond)
//   - front sheet stays in place (so the thumbnail doesn't jitter)
//   - everything respects `prefers-reduced-motion`.

import { Link04Icon, MoreHorizontalIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { type Ref, useMemo } from "react";

import { useRoughPath } from "@/components/rough/useRoughPath";
import type { FileKind } from "@/lib/api/client";
import { cn } from "@/lib/utils";

import { FileKindBadge, FileKindGlyph } from "./file-kind-icons";
import { tiltFromId } from "./tilt";
import { buildTornCorner } from "./tornCorner";

// Front-sheet rendering size in viewBox px. Used by `useRoughPath` to
// generate per-card wobbly edges; the resulting <path>s are rendered in
// this coordinate space and stretched to fit via `preserveAspectRatio`.
const SHEET_W = 200;
const SHEET_H = 150;
// Back-stack inset (in viewBox px) on each side. The two sheets behind
// the front sit inset by this much so at rest they hide behind it; on
// hover they fan out via the `.ink-file__back--*` CSS rules.
const BACK_INSET = 8;

export interface FileCardProps {
  id: string;
  name: string;
  kind: FileKind;
  hasThumb: boolean;
  thumbUrl: string;
  /** Parent folder name. Currently unused in the visual (Recent shows
   *  it in the right-click menu) but kept on the prop for future use. */
  folderName?: string | null;
  updatedAtLabel: string;
  /** File tags are still passed through from list data, but are not shown on the card. */
  tags: string[];
  /** Slot for a hover-revealed actions trigger (DropdownMenu trigger). */
  actions?: React.ReactNode;
  /** Single click — opens the file. */
  onOpen?: () => void;
  /** Right click — open the context menu. */
  onContextMenu?: React.MouseEventHandler<HTMLButtonElement>;
  /** Number of currently-active share tokens for this file. When > 0
   *  a small "shared" pill is rendered alongside the kind badge. */
  activeShareCount?: number;
  /** Click handler for the share pill. Receives the click event so the
   *  caller can stop propagation if it wraps the card in another
   *  click target (e.g. ContextMenuTrigger). */
  onOpenShare?: () => void;
  className?: string;
}

export function FileCard({
  id,
  name,
  kind,
  hasThumb,
  thumbUrl,
  updatedAtLabel,
  actions,
  onOpen,
  onContextMenu,
  activeShareCount = 0,
  onOpenShare,
  className,
  ref,
}: FileCardProps & { ref?: Ref<HTMLDivElement> }) {
  const tilt = tiltFromId(`file:${id}`, 0.4);

  return (
    <div
      ref={ref}
      data-explorer-item="file"
      data-explorer-id={id}
      title={updatedAtLabel ? `${name} · ${updatedAtLabel}` : name}
      className={cn(
        "group/file relative flex flex-col items-center gap-2 rounded-md",
        "focus-within:ring-2 focus-within:ring-ring/60",
        className,
      )}
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      <button
        type="button"
        aria-label={`File: ${name}`}
        onClick={onOpen}
        onContextMenu={onContextMenu}
        className={cn(
          "flex w-full flex-col items-center gap-2 rounded-md bg-transparent p-2",
          "focus-visible:outline-none",
        )}
      >
        <FileGlyph id={id} kind={kind} hasThumb={hasThumb} thumbUrl={thumbUrl} />

        <div className="w-full min-w-0 text-center">
          <div
            className="line-clamp-2 min-h-[2.5em] font-heading text-sm font-medium leading-tight text-foreground"
            title={name}
          >
            {name}
          </div>
        </div>
      </button>

      {/* Top-left cluster: kind badge always; share pill conditionally.
          Both live inside one flex container so they never overlap. */}
      <div className="absolute left-1 top-1 z-10 flex items-center gap-1">
        <FileKindBadge kind={kind} />
        {activeShareCount > 0 ? <SharePill count={activeShareCount} onClick={onOpenShare} /> : null}
      </div>

      {actions ? (
        <div className="absolute right-1 top-1 z-10 opacity-0 transition-opacity group-hover/file:opacity-100 focus-within:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Top-left pill on a card that signals "this item has active share
 * links." Always visible (not hover-revealed) so the user can spot
 * shared items at a glance. Click opens the per-target ShareDialog.
 */
function SharePill({ count, onClick }: { count: number; onClick?: () => void }) {
  const label = count === 1 ? "1 active link" : `${count} active links`;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onContextMenu={(e) => e.stopPropagation()}
      title={label}
      aria-label={label}
      className="inline-flex h-5 items-center gap-1 rounded-full bg-accent/70 px-1.5 text-[0.625rem] font-medium text-accent-foreground ring-1 ring-border/50 backdrop-blur-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
    >
      <HugeiconsIcon icon={Link04Icon} strokeWidth={2} className="size-2.5" />
      <span>{count}</span>
    </button>
  );
}

/**
 * Paper-sheet silhouette with a torn top-right corner, plus two
 * stacked sheets behind that fan out on hover. The torn polygon is
 * shared by all three sheets so they look like a notepad torn
 * through (not three pages with independent rips).
 *
 * Layer order (back → front):
 *   1. Back-stack sheets  (SVG <path> using torn shape, inset 8px,
 *                          hidden at rest, fanned on hover)
 *   2. Front sheet fill+outline (rough.js around the torn polygon,
 *                                 CSS drop-shadow lifts it off the desk)
 *   3. Thumbnail          (HTML <img>, clip-path = torn polygon)
 *   4. Paper grain        (div, .bg-paper-grain, multiply/screen blend,
 *                          clip-path = torn polygon)
 *   5. Paper-fibre dots   (div, .bg-paper-dots, multiply/screen blend,
 *                          clip-path = torn polygon)
 */
function FileGlyph({
  id,
  kind,
  hasThumb,
  thumbUrl,
}: {
  id: string;
  kind: FileKind;
  hasThumb: boolean;
  thumbUrl: string;
}) {
  // Torn-corner geometry. Computed once per file id; both `pathD`
  // (viewBox units, fed to rough.js + back-stack <path>s) and
  // `clipPolygon` (CSS percentages, applied to HTML overlays) are
  // derived from the same vertex list so the silhouette and clip
  // always agree.
  const torn = useMemo(
    () =>
      buildTornCorner({
        width: SHEET_W,
        height: SHEET_H,
        seed: `file-tear:${id}`,
      }),
    [id],
  );

  // The back-stack sheets sit inset by `BACK_INSET` on every side. We
  // reuse the same torn vertex list, but scaled to fit the inset box
  // and translated by (BACK_INSET, BACK_INSET). Doing this via an SVG
  // `transform` on the <path> would also scale the visible stroke
  // weight, so we compensate by dividing the stroke width by the
  // smaller scale factor.
  const backSx = (SHEET_W - 2 * BACK_INSET) / SHEET_W;
  const backSy = (SHEET_H - 2 * BACK_INSET) / SHEET_H;
  const backStroke = 1.2 / Math.min(backSx, backSy);

  // Hand-drawn front-sheet paths from rough.js, drawn around the torn
  // polygon. Low roughness keeps the wobble subtle (~1 viewBox-px) so
  // the tear stays readable as a clean shape with paper-fibre fuzz,
  // not a chaotic scribble.
  const paperPaths = useRoughPath({
    width: SHEET_W,
    height: SHEET_H,
    shape: "custom",
    customPathD: torn.pathD,
    seed: `file-paper:${id}`,
    stroke: "var(--color-card-stroke)",
    strokeWidth: 1.3,
    fill: "var(--color-card)",
    fillStyle: "solid",
    roughness: 0.6,
    bowing: 0.4,
  });

  return (
    <div className="relative w-full" style={{ aspectRatio: "4 / 3" }} aria-hidden>
      {/* 1. Back stack — two sheets that fan out on hover. Inset on
            every side from the front sheet so at rest they hide
            *behind* the front silhouette, and on hover peek out from
            behind (not beyond) the front. They share the front's
            torn polygon so the tear cascades through the stack. */}
      <svg
        viewBox={`0 0 ${SHEET_W} ${SHEET_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full overflow-visible"
        role="presentation"
      >
        <title>File back stack</title>
        <g transform={`translate(${BACK_INSET} ${BACK_INSET}) scale(${backSx} ${backSy})`}>
          <path
            className="ink-file__back ink-file__back--l"
            d={torn.pathD}
            fill="var(--color-card)"
            stroke="var(--color-card-stroke)"
            strokeOpacity="0.55"
            strokeWidth={backStroke}
            strokeLinejoin="round"
          />
          <path
            className="ink-file__back ink-file__back--r"
            d={torn.pathD}
            fill="var(--color-card)"
            stroke="var(--color-card-stroke)"
            strokeOpacity="0.55"
            strokeWidth={backStroke}
            strokeLinejoin="round"
          />
        </g>
      </svg>

      {/* 2. Front sheet — hand-drawn fill + outline from rough.js,
            drawn around the torn polygon. CSS drop-shadow lifts it
            off the desk (a tight contact shadow + a soft ambient one
            read in both light and dark themes). The drop-shadow
            filter follows the actual rendered alpha, so the tear
            casts a tear-shaped shadow — not a clean rectangle. */}
      <svg
        viewBox={`0 0 ${SHEET_W} ${SHEET_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full overflow-visible"
        style={{
          filter:
            "drop-shadow(0 1px 1.5px rgba(0,0,0,0.22)) drop-shadow(0 6px 10px rgba(0,0,0,0.16))",
        }}
        role="presentation"
      >
        <title>File paper</title>
        {paperPaths.map((p) => (
          <path
            key={`${p.stroke ?? ""}|${p.fill ?? ""}|${p.d}`}
            d={p.d}
            stroke={p.stroke}
            strokeWidth={p.strokeWidth}
            fill={p.fill ?? "none"}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>

      {/* 3. Thumbnail clipped to the torn silhouette. No inset — we
            *want* the image to bleed all the way to the torn edge so
            the tear doesn't reveal a clean rectangle hidden under it.
            For files without a thumbnail (yet) we fall back to a muted
            kind glyph so the placeholder still hints at the file type. */}
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: torn.clipPolygon }}>
        {hasThumb ? (
          <img
            src={thumbUrl}
            alt=""
            loading="lazy"
            draggable={false}
            className="ink-thumb-img h-full w-full object-cover object-center"
          />
        ) : (
          <div className="grid h-full w-full place-items-center">
            <FileKindGlyph
              kind={kind}
              variant="full"
              className="size-12 rounded-md ring-1 ring-border/50"
            />
          </div>
        )}
      </div>

      {/* 4. Paper grain — fine tooth, reuses the utility from
            <PaperSurface>. Mix-blend-mode in the class flips per theme. */}
      <div
        aria-hidden
        className="bg-paper-grain pointer-events-none absolute inset-0"
        style={{ clipPath: torn.clipPolygon }}
      />

      {/* 5. Paper-fibre dots — sparse, larger specks that read as
            recycled / kraft paper inclusions. Stacked on top of grain. */}
      <div
        aria-hidden
        className="bg-paper-dots pointer-events-none absolute inset-0"
        style={{ clipPath: torn.clipPolygon }}
      />
    </div>
  );
}

/** Convenience icon for a "more actions" trigger. */
export function FileCardActionsIcon() {
  return <HugeiconsIcon icon={MoreHorizontalIcon} strokeWidth={2} />;
}
